// pipe (list findability): the SHARED, pure, React-free WIDENING engine — the one seam behind
// "typing in a list screen's filter box must reach rows that are not on the loaded page".
//
// Every ERP grid loads a PAGE of the newest rows and filters that page in the browser. At
// production scale (117k parts, 130k lots, 252k items) the row an operator is looking for is
// almost never on it, and under `PIPE_STAGE_ACCESS_POLICY=full_isolation` a station-scoped
// operator's page can legitimately be EMPTY — so a client-only filter answers "no rows match" for
// a row that exists. The fix is to WIDEN the same term server-side through the one Erp-gated +
// facility/account-partitioned + redaction-at-WHERE `search_core` (type-narrowed since the server
// half of this slice), and to say honestly what came from where.
//
// This module owns ONLY the timing rules — the part that used to be re-hand-rolled per screen and
// is exactly the part that breaks under load. It was lifted verbatim (behaviour-for-behaviour)
// out of the Reports ref-picker engine, which now consumes it:
//
//   * DEBOUNCED — one request per typing pause, never one per keystroke.
//   * LATEST-WINS per key — a slow response for an older term can never land over a newer one (the
//     standing frontend rule: every list/picker fetch carries a cancelled flag or a latest-wins seq).
//   * NON-DESTRUCTIVE — hits are additive; a failed or superseded widen leaves the loaded page
//     exactly as it was, never a blank list.
//   * DEFAULTS RESTORED — a cleared or too-short term cancels anything pending and re-emits the
//     caller's default source.
//   * HONEST — every outcome a screen could mistake for "searched, found nothing" is its OWN state,
//     because the one thing this module must never produce is a confident wrong answer: `searching`
//     is reported from the KEYSTROKE (not from the request), so a list never says "nothing matched"
//     while a widen for that very term is still pending; `capped` reports a result that came back AT
//     the server ceiling (more matches exist than were fetched — the no-silent-partial rule);
//     `failed` means the search errored; `tooShort` means no search was ever ISSUED; and
//     `filteredOut` means the server found matches that THIS screen's own narrowing excluded.
//
// It is NOT a security boundary. Every row it carries came from the shared server search, already
// authorized and already redacted; `accept` below only ever REMOVES rows the actor could already
// retrieve (a scoping convenience, exactly like the client-side row filter in erp-table-filter.ts).

/**
 * The minimal structural row the widen machinery defaults to. @affine/core's `RecordRow` is
 * MUTUALLY assignable with it (its extras are all optional), so `ErpWiden` written bare on either
 * side interoperates without a cast — the engine itself only ever touches `id`.
 */
export interface ErpRow {
  id: string;
  type: string;
  title: string | null;
  data: Record<string, unknown>;
}

/** What a list/picker reports about its OWN widening. */
export interface WidenState {
  /** A widen for the current term is pending (debouncing) or in flight. */
  searching: boolean;
  /** The last widen came back AT the server ceiling — more matches exist than were fetched. */
  capped: boolean;
  /** The last widen FAILED. A DISTINCT state from "searched, found nothing extra": without it the
   *  screen narrows the loaded page client-side, finds nothing and states the row does not exist —
   *  a server error rendered as a fact. */
  failed: boolean;
  /** The term is NON-EMPTY but below `minLength`, so NO search ran. A DISTINCT state from "searched,
   *  found nothing extra" for exactly the same reason `failed` is: collapsing it into the idle state
   *  let a screen answer a one-character term with "searched every record, not just this page" for a
   *  search that was never issued — the confident-wrong-answer this module exists to eliminate. */
  tooShort: boolean;
  /** The server DID return matches for this term and THIS SCREEN's own narrowing (`accept`) dropped
   *  some — e.g. the operations worklist excluding imported history, or a job-scoped Records grid
   *  excluding other jobs. Also distinct from "found nothing": the record exists and is reachable,
   *  just not from this list, so the screen can point at it instead of denying it. */
  filteredOut: boolean;
}
export const IDLE_WIDEN: WidenState = {
  searching: false,
  capped: false,
  failed: false,
  tooShort: false,
  filteredOut: false,
};
/** Non-empty but under `minLength`: nothing was asked, and a view must say so rather than claim
 *  the whole list was searched. */
export const TOO_SHORT_WIDEN: WidenState = {
  searching: false,
  capped: false,
  failed: false,
  tooShort: true,
  filteredOut: false,
};

/** Cheap identity check so a caller can skip a `setState` that would change nothing. */
export function sameWidenState(a: WidenState, b: WidenState): boolean {
  return (
    a.searching === b.searching &&
    a.capped === b.capped &&
    a.failed === b.failed &&
    a.tooShort === b.tooShort &&
    a.filteredOut === b.filteredOut
  );
}

/**
 * What a CONTAINER hands a pure view so the view can widen without ever touching DI.
 *
 * Four members — deliberately the whole contract: the keystroke sink, the latest-wins hits, the
 * honest state, and the mutation splice that keeps a found row as fresh as a page row. A view that
 * receives `undefined` renders exactly as it did before this slice (a screen with no server-side
 * widen is a legal, unchanged screen).
 */
export interface ErpWiden<T extends { id: string } = ErpRow> {
  /** Every keystroke (raw, undebounced — the engine owns the timing). */
  onQuery: (term: string) => void;
  /** Latest-wins server hits for the current term — already authorized + redacted rows. */
  hits: T[];
  state: WidenState;
  /**
   * Swap a mutated row's FRESH server projection into the hits, in place.
   *
   * The standing frontend rule ("a single-row mutation response is the same one-serializer
   * projection as a list row → splice it in place") applies to a row found by SEARCH exactly as it
   * does to a row on the loaded page — and a widened row is by construction NOT in the page, so a
   * container that only splices its own `rows` leaves the hit rendering its pre-mutation state and
   * its stale `allowed_transitions` (a second click then 409s for a transition that already
   * happened, and a second edit re-submits the pre-edit snapshot). REPLACE-only: a row that is not
   * already a hit is never added, so a mutation can never widen what the search returned.
   */
  splice: (row: T) => void;
}

export const WIDEN_DEBOUNCE_MS = 300;
/** Below this the term is not worth a round trip. */
export const WIDEN_MIN_LENGTH = 2;
/** The server clamps `/search` at `SEARCH_LIMIT_MAX` (200) regardless; asking for exactly it makes
 *  the ceiling explicit AND makes `hits.length >= limit` a truthful `capped` test — a LARGER ask is
 *  silently clamped, which would make the comparison wrong. */
export const WIDEN_LIMIT = 200;

export interface WidenResult<T> {
  /** The raw, latest-wins server hits for the current term (`[]` when cleared / too short /
   *  failed). A list screen renders these as its OWN, separately-labelled group. */
  hits: T[];
  /** `defaults` with `hits` merged over them by id, defaults first — the PICKER contract (a picker
   *  offers one flat option list and must not lose what it already offered). */
  rows: T[];
}

export interface ListWidenDeps<T extends { id: string }> {
  /** Run the server-side widen for `key`. Rejects ⇒ `failed`. */
  search: (
    key: string,
    term: string,
    limit: number
  ) => Promise<T[] | { rows: T[]; has_more: boolean }>;
  onResult: (key: string, result: WidenResult<T>) => void;
  /** May be called with an equal value — callers de-dupe with `sameWidenState`. */
  onState: (key: string, state: WidenState) => void;
  /** Optional per-key narrowing of the hits (e.g. "only rows tagged to THIS order"). It can only
   *  drop rows the server already authorized — never a boundary, and never a widen. */
  accept?: (key: string, row: T) => boolean;
  debounceMs?: number;
  minLength?: number;
  limit?: number;
}

export interface ListWiden<T extends { id: string }> {
  /** Install the default source for `key` (the loaded page) — what a cleared term restores and what
   *  `WidenResult.rows` merges over. Optional: a screen that keeps its page separate never calls it. */
  setDefault: (key: string, rows: T[]) => void;
  /** The user typed (or a picker closed, with `''`). Debounced, latest-wins; a short/empty term
   *  cancels anything pending and restores the default source. */
  query: (key: string, term: string) => void;
  /** Drop pending timers and invalidate in-flight responses (unmount). */
  dispose: () => void;
}

interface KeyState<T> {
  defaults: T[];
  seq: number;
  timer: ReturnType<typeof setTimeout> | null;
}

/** Defaults first, hits merged over them by id (a hit REPLACES its stale default — it is the fresher
 *  server projection), each row exactly once. */
export function mergeById<T extends { id: string }>(
  defaults: readonly T[],
  hits: readonly T[]
): T[] {
  const byId = new Map(defaults.map(r => [r.id, r] as const));
  for (const row of hits) byId.set(row.id, row);
  return [...byId.values()];
}

/** The rows a widen ADDS below what a screen is already showing: server hits that are not already
 *  displayed. Deliberately deduped against the DISPLAYED rows (not against the whole page): a hit
 *  the client-side filter dropped — the two matchers do not agree by construction — must still
 *  reach the operator, exactly once. */
export function widenExtras<T extends { id: string }>(
  displayed: readonly T[],
  hits: readonly T[]
): T[] {
  if (hits.length === 0) return [];
  const shown = new Set(displayed.map(r => r.id));
  return hits.filter(r => !shown.has(r.id));
}

/**
 * The DEEP-LINK PIN: the row the operator explicitly asked for, first and never filtered away.
 *
 * A scan card / global-search pick routes to a LIST screen carrying the picked row's `id`; the
 * screen fetches that row by id (the ordinary scoped + redacted single-row read) and pins it here,
 * because the loaded page is the newest ~200 rows of a type that can hold hundreds of thousands —
 * and under FullIsolation the page can be empty outright. Deduped, so a pinned row that IS inside
 * the page renders once (at the top), not twice.
 *
 * `focused` is the CALLER's already-guarded row: each screen decides what "belongs here" means
 * (Records guards on the selected type, the worklist on the station), because pinning a foreign
 * row into a grid renders a row of blank columns.
 */
export function pinFirst<T extends { id: string }>(
  rows: readonly T[],
  focused: T | null | undefined
): T[] {
  if (!focused) return rows as T[];
  return [focused, ...rows.filter(r => r.id !== focused.id)];
}

export function createListWiden<T extends { id: string } = ErpRow>(
  deps: ListWidenDeps<T>
): ListWiden<T> {
  const debounceMs = deps.debounceMs ?? WIDEN_DEBOUNCE_MS;
  const minLength = deps.minLength ?? WIDEN_MIN_LENGTH;
  const limit = deps.limit ?? WIDEN_LIMIT;
  const keys = new Map<string, KeyState<T>>();
  const stateFor = (key: string): KeyState<T> => {
    let s = keys.get(key);
    if (!s) {
      s = { defaults: [], seq: 0, timer: null };
      keys.set(key, s);
    }
    return s;
  };

  /** Bumping the sequence is what makes an older response a no-op; clearing the timer is what makes
   *  an older PENDING request never happen at all. Both, always, before starting anything new. */
  const invalidate = (key: string): number => {
    const s = stateFor(key);
    if (s.timer !== null) {
      clearTimeout(s.timer);
      s.timer = null;
    }
    s.seq += 1;
    return s.seq;
  };

  const setDefault = (key: string, rows: T[]) => {
    stateFor(key).defaults = rows;
  };

  const query = (key: string, term: string) => {
    const s = stateFor(key);
    const seq = invalidate(key);
    const trimmed = term.trim();
    if (trimmed.length < minLength) {
      // Cleared or too short to search: back to the page the initial load fetched. The two cases
      // are reported DIFFERENTLY — an empty box has nothing to say, while a one-character term is
      // an un-run search a view must never present as an exhausted one.
      deps.onState(key, trimmed.length === 0 ? IDLE_WIDEN : TOO_SHORT_WIDEN);
      deps.onResult(key, { hits: [], rows: s.defaults });
      return;
    }
    // Announced from the KEYSTROKE, not from the request: the pause before the request is exactly
    // when a screen would otherwise flash "no rows match" for a term it has not searched yet.
    deps.onState(key, {
      searching: true,
      capped: false,
      failed: false,
      tooShort: false,
      filteredOut: false,
    });
    deps.onResult(key, { hits: [], rows: s.defaults });
    s.timer = setTimeout(() => {
      s.timer = null;
      deps.search(key, trimmed, limit).then(
        result => {
          const rows = Array.isArray(result) ? result : result.rows;
          if (s.seq !== seq) return;
          const accept = deps.accept;
          const hits = accept ? rows.filter(r => accept(key, r)) : rows;
          deps.onState(key, {
            searching: false,
            // Measured on what the SERVER returned, before any client narrowing — `accept`
            // dropping rows does not mean the server had more to give.
            capped: Array.isArray(result)
              ? rows.length >= limit
              : result.has_more,
            failed: false,
            tooShort: false,
            // This screen's own narrowing removed matches the server DID return: the row exists,
            // it just does not belong to this list — never rendered as "nothing matches".
            filteredOut: hits.length < rows.length,
          });
          deps.onResult(key, { hits, rows: mergeById(s.defaults, hits) });
        },
        () => {
          // A failed widen leaves the default page in place — the screen never goes blank — but it
          // is REPORTED as a failure, not as an idle empty result (see the module header).
          if (s.seq !== seq) return;
          deps.onState(key, {
            searching: false,
            capped: false,
            failed: true,
            tooShort: false,
            filteredOut: false,
          });
          deps.onResult(key, { hits: [], rows: s.defaults });
        }
      );
    }, debounceMs);
  };

  const dispose = () => {
    for (const key of keys.keys()) invalidate(key);
  };

  return { setDefault, query, dispose };
}
